
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { ColumnConfig } from '../types';

/**
 * What an export says about itself beyond its title — whose report it is, for
 * what period, and any labelled facts about the selection. All optional: a grid
 * that passes nothing still exports, under the department's own name.
 */
export interface ExportHeader {
  /** Whose books — a UDA name. Leads the header when present. */
  authority?: string;
  /** One line under the title: the period, a count, a note. */
  subtitle?: string;
  /** Labelled facts ('Status: Pending') shown under the heading. */
  criteria?: { label: string; value: string }[];
}

const DEPARTMENT = 'Directorate of Town & Country Planning';
const GOVERNMENT = 'Government of Andhra Pradesh';

// Helper to check if a column is a serial number column
const isSerialColumn = (columnName: string, field: string): boolean => {
  const serialPatterns = [
    /^s\.?no\.?$/i, /^sr\.?no\.?$/i, /^sl\.?no\.?$/i,
    /^serial\.?(no\.?|number)?$/i, /^#$/, /^no\.?$/i,
    /^sno$/i, /^srno$/i, /^slno$/i,
  ];
  const nameToCheck = (columnName || field || '').trim();
  return serialPatterns.some(pattern => pattern.test(nameToCheck));
};

export const exportToCSV = (data: any[], columns: ColumnConfig[], filename: string = 'export.csv', title?: string) => {
  // Filter and sort columns based on export settings and order
  const exportableColumns = columns
    .filter(col => col.exportable !== false && col.columnType !== 'button') // Exclude button columns
    .sort((a, b) => (a.order || 0) - (b.order || 0)); // Sort by order

  // Create filtered data with proper column headers and sequential serial numbers
  const filteredData = data.map((row, index) => {
    const filteredRow: any = {};
    exportableColumns.forEach(col => {
      const field = col.field || col.columnName;
      if (field) {
        // Generate sequential serial number for serial columns
        if (isSerialColumn(col.columnName, field)) {
          filteredRow[col.columnName] = index + 1;
        } else if (col.exportValue) {
          // Use custom exportValue function if provided
          filteredRow[col.columnName] = col.exportValue(row);
        } else {
          filteredRow[col.columnName] = row[field];
        }
      }
    });
    return filteredRow;
  });

  const ws = XLSX.utils.json_to_sheet(filteredData);
  const csv = XLSX.utils.sheet_to_csv(ws);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, filename);
};

export const exportToExcel = (data: any[], columns: ColumnConfig[], filename: string = 'export.xlsx', title?: string, header?: ExportHeader) => {
  // Filter and sort columns based on export settings and order
  const exportableColumns = columns
    .filter(col => col.exportable !== false && col.columnType !== 'button') // Exclude button columns
    .sort((a, b) => (a.order || 0) - (b.order || 0)); // Sort by order

  // Create filtered data with proper column headers and sequential serial numbers
  const filteredData = data.map((row, index) => {
    const filteredRow: any = {};
    exportableColumns.forEach(col => {
      const field = col.field || col.columnName;
      if (field) {
        // Generate sequential serial number for serial columns
        if (isSerialColumn(col.columnName, field)) {
          filteredRow[col.columnName] = index + 1;
        } else if (col.exportValue) {
          // Use custom exportValue function if provided
          filteredRow[col.columnName] = col.exportValue(row);
        } else {
          filteredRow[col.columnName] = row[field];
        }
      }
    });
    return filteredRow;
  });

  const wb = XLSX.utils.book_new();

  // Create worksheet with title row if title is provided
  const ws = XLSX.utils.json_to_sheet([]);

  let dataStartRow = 1;
  if (title) {
    // The same heading the PDF carries: office, (department), title, period, criteria.
    const headRows: string[][] = [[header?.authority || DEPARTMENT]];
    if (header?.authority) headRows.push([`${DEPARTMENT}, ${GOVERNMENT}`]);
    headRows.push([title]);
    if (header?.subtitle) headRows.push([header.subtitle]);
    (header?.criteria ?? []).filter(c => c.value).forEach(c => headRows.push([`${c.label}: ${c.value}`]));
    XLSX.utils.sheet_add_aoa(ws, headRows, { origin: 'A1' });
    // The title row spans the table, so it reads as a heading rather than a stray cell.
    const lastCol = Math.max(exportableColumns.length - 1, 0);
    ws['!merges'] = headRows.map((_, r) => ({ s: { r, c: 0 }, e: { r, c: lastCol } }));
    // One empty row, then the data.
    dataStartRow = headRows.length + 2;
    XLSX.utils.sheet_add_json(ws, filteredData, { origin: `A${dataStartRow}` });
  } else {
    XLSX.utils.sheet_add_json(ws, filteredData, { origin: 'A1' });
  }

  // Calculate the row after data ends
  const footerStartRow = dataStartRow + filteredData.length + 2;

  // Add footer
  XLSX.utils.sheet_add_aoa(ws, [[`Total Records: ${data.length}`]], { origin: `A${footerStartRow}` });
  XLSX.utils.sheet_add_aoa(ws, [[`Generated: ${new Date().toLocaleString()}`]], { origin: `A${footerStartRow + 1}` });
  XLSX.utils.sheet_add_aoa(ws, [['Directorate of Town & Country Planning, Government of Andhra Pradesh']], { origin: `A${footerStartRow + 2}` });

  const sheetName = title ? title.substring(0, 31) : 'Sheet1'; // Excel sheet name max 31 chars
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
  saveAs(blob, filename);
};

// Helper to determine if a column should allow text wrapping
const isWrapColumn = (col: ColumnConfig): boolean => {
  const field = (col.field || '').toLowerCase();
  const name = (col.columnName || '').toLowerCase();
  // Only wrap description, remarks, address, notes type columns
  const wrapPatterns = ['description', 'remarks', 'address', 'notes', 'comment', 'details'];
  return wrapPatterns.some(pattern => field.includes(pattern) || name.includes(pattern));
};

// Helper to determine if column is a primary name/title column (should get good width)
const isNameColumn = (col: ColumnConfig): boolean => {
  const field = (col.field || '').toLowerCase();
  const name = (col.columnName || '').toLowerCase();
  const namePatterns = ['name', 'title', 'designation', 'department'];
  return namePatterns.some(pattern => field === pattern || name.includes(pattern));
};

// Helper to determine if a column should be narrow (no wrap)
const isNarrowColumn = (col: ColumnConfig): boolean => {
  const field = (col.field || '').toLowerCase();
  const name = (col.columnName || '').toLowerCase();
  // Don't make name/title columns narrow
  if (isNameColumn({ field, columnName: name } as ColumnConfig)) return false;
  // Serial, date, and short identifier columns
  const narrowPatterns = ['sno', 'slno', 'srno', 'serial', 'code', 'version', 'status'];
  return isSerialColumn(col.columnName, field) || narrowPatterns.some(pattern => field.includes(pattern) || name.includes(pattern));
};

// Helper to check if column is a date/timestamp column
const isDateColumn = (col: ColumnConfig): boolean => {
  const field = (col.field || '').toLowerCase();
  const name = (col.columnName || '').toLowerCase();
  const datePatterns = ['date', 'createdon', 'modifiedon', 'updatedon', 'createddate', 'lastmodifieddate', 'timestamp'];
  return datePatterns.some(pattern => field.includes(pattern) || name.includes(pattern));
};

export const exportToPDF = async (data: any[], columns: ColumnConfig[], filename: string = 'export.pdf', title?: string, header?: ExportHeader) => {
  // AP state branding (header logo + per-page watermark) — applied below.
  const { addApHeaderLogo, addApWatermark } = await import('../../../utils/apPdfBranding');
  // Filter and sort columns based on export settings and order
  const exportableColumns = columns
    .filter(col => col.exportable !== false && col.columnType !== 'template' && col.columnType !== 'button') // Exclude template and button columns
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const numColumns = exportableColumns.length;

  // Always use landscape for better fit
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4'
  });

  // Prepare table headers
  const headers = [exportableColumns.map(col => col.columnName)];

  // Prepare table data with sequential serial numbers
  const tableData = data.map((row, index) => {
    return exportableColumns.map(col => {
      const field = col.field || col.columnName;

      // Generate sequential serial number for serial columns
      if (isSerialColumn(col.columnName, field)) {
        return String(index + 1);
      }

      // Use custom exportValue function if provided
      if (col.exportValue) {
        const exportedValue = col.exportValue(row);
        return exportedValue === null || exportedValue === undefined ? '-' : String(exportedValue);
      }

      let value = row[field];

      // Handle different data types
      if (value === null || value === undefined) {
        return '-';
      }

      // Handle boolean values
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }

      // Handle objects (convert to string)
      if (typeof value === 'object') {
        return JSON.stringify(value);
      }

      return String(value);
    });
  });

  // Header emblem geometry. The AP logo is stamped at the very end of this
  // function (addApHeaderLogo below) but its box has to be reserved HERE,
  // because the title is drawn first and jsPDF has no z-order — whatever is
  // drawn last wins. The title used to start at x=14, inside the logo's
  // 4..18mm span, so the emblem painted over its first character.
  const LOGO_X = 10;
  const LOGO_Y = 5;
  const LOGO_SIZE = 15;

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  /*
   * THE LETTERHEAD. Emblem at the left, everything else centred, so the file
   * reads as an office document rather than a screen dump:
   *
   *   [emblem]        <UDA, or the Directorate>
   *                   Directorate of Town & Country Planning, Government of AP
   *                   <Report title>
   *                   <period / note>
   *   ------------------------------------------------------------------
   *   Label: value    Label: value    Label: value
   *
   * The emblem is stamped last (jsPDF has no z-order), so its box is simply
   * left clear here.
   */
  const displayTitle = title || 'Report';
  const cx = pageWidth / 2;
  let headY = 10;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(31, 41, 55);
  doc.text(header?.authority || DEPARTMENT, cx, headY, { align: 'center' });
  if (header?.authority) {
    headY += 4.5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(90, 90, 90);
    doc.text(`${DEPARTMENT}, ${GOVERNMENT}`, cx, headY, { align: 'center' });
  }
  headY += 5.5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(31, 41, 55);
  doc.text(displayTitle, cx, headY, { align: 'center' });
  if (header?.subtitle) {
    headY += 4.5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(90, 90, 90);
    doc.text(header.subtitle, cx, headY, { align: 'center' });
  }
  headY = Math.max(headY, LOGO_Y + LOGO_SIZE) + 3;
  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.2);
  doc.line(10, headY, pageWidth - 10, headY);

  const criteria = (header?.criteria ?? []).filter(c => c.value);
  if (criteria.length) {
    const cols = 3;
    const colW = (pageWidth - 20) / cols;
    doc.setFontSize(8);
    criteria.forEach((c, i) => {
      const x = 10 + (i % cols) * colW;
      const y = headY + 4.5 + Math.floor(i / cols) * 4.2;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(31, 41, 55);
      doc.text(`${c.label}:`, x, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(90, 90, 90);
      doc.text(String(c.value), x + doc.getTextWidth(`${c.label}: `) + 1, y, { maxWidth: colW - doc.getTextWidth(`${c.label}: `) - 4 });
    });
    headY += 4.5 + Math.ceil(criteria.length / cols) * 4.2;
    doc.line(10, headY, pageWidth - 10, headY);
  }
  const tableStartY = headY + 4;
  const generatedDate = new Date().toLocaleString();
  const totalRecords = data.length;

  // Calculate available width for table
  const availableWidth = pageWidth - 20; // 10mm margin on each side

  // Auto-calculate font size based on number of columns
  let fontSize = 8;
  if (numColumns > 10) fontSize = 6;
  else if (numColumns > 8) fontSize = 7;
  else if (numColumns <= 5) fontSize = 9;

  // Calculate column widths - narrow columns get fixed small width, rest distribute evenly
  const columnStyles: { [key: number]: any } = {};
  let narrowColumnsWidth = 0;
  let wrapColumnsCount = 0;
  let normalColumnsCount = 0;

  exportableColumns.forEach((col, index) => {
    if (isSerialColumn(col.columnName, col.field || '')) {
      columnStyles[index] = { cellWidth: 10, halign: 'center', overflow: 'visible' };
      narrowColumnsWidth += 10;
    } else if (isDateColumn(col)) {
      // Date/timestamp columns need more width (e.g., "27-12-2025 14:30:45")
      columnStyles[index] = { cellWidth: 32, halign: 'center', overflow: 'visible' };
      narrowColumnsWidth += 32;
    } else if (isNameColumn(col)) {
      // Name/title columns should get good width
      normalColumnsCount++;
      columnStyles[index] = { halign: 'left', overflow: 'visible', minCellWidth: 35 };
    } else if (isNarrowColumn(col)) {
      // Other narrow columns (code, status, etc.)
      const width = col.columnName.length > 10 ? 24 : 18;
      columnStyles[index] = { cellWidth: width, halign: 'center', overflow: 'visible' };
      narrowColumnsWidth += width;
    } else if (isWrapColumn(col)) {
      wrapColumnsCount++;
      columnStyles[index] = { halign: 'left', overflow: 'linebreak' };
    } else {
      normalColumnsCount++;
      columnStyles[index] = { halign: 'left', overflow: 'visible' };
    }
  });

  // Distribute remaining width among wrap and normal columns
  const remainingWidth = availableWidth - narrowColumnsWidth;
  const totalFlexColumns = wrapColumnsCount + normalColumnsCount;

  if (totalFlexColumns > 0) {
    const flexWidth = remainingWidth / totalFlexColumns;
    exportableColumns.forEach((col, index) => {
      if (!columnStyles[index].cellWidth) {
        if (isWrapColumn(col)) {
          columnStyles[index].cellWidth = Math.max(flexWidth, 30); // Min 30mm for wrap columns
        } else if (isNameColumn(col)) {
          columnStyles[index].cellWidth = Math.max(flexWidth, 35); // Min 35mm for name/title columns
        } else {
          columnStyles[index].cellWidth = Math.max(flexWidth, 20); // Min 20mm for normal columns
        }
      }
    });
  }

  // Add table with footer on each page
  autoTable(doc, {
    head: headers,
    body: tableData,
    startY: tableStartY,
    theme: 'grid',
    styles: {
      fontSize: fontSize,
      cellPadding: 1.5,
      lineColor: [200, 200, 200],
      lineWidth: 0.1,
      overflow: 'visible',
      cellWidth: 'auto',
    },
    headStyles: {
      fillColor: [59, 130, 246], // Blue color
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'center',
      fontSize: fontSize,
      cellPadding: 2,
      overflow: 'visible',
      minCellHeight: 8,
    },
    columnStyles: columnStyles,
    alternateRowStyles: {
      fillColor: [249, 250, 251] // Light gray for alternate rows
    },
    margin: { top: 18, left: 10, right: 10, bottom: 22 },
    tableWidth: 'auto',
    didParseCell: (data) => {
      // For body cells, truncate long text in non-wrap columns
      // But never truncate name/title columns - they should show full text
      if (data.section === 'body') {
        const colIndex = data.column.index;
        const col = exportableColumns[colIndex];
        if (!isWrapColumn(col) && !isNarrowColumn(col) && !isNameColumn(col)) {
          const text = String(data.cell.raw || '');
          if (text.length > 25) {
            data.cell.text = [text.substring(0, 22) + '...'];
          }
        }
      }
    },
    didDrawPage: (data) => {
      // Footer on each page
      const currentPage = (doc as any).internal.getCurrentPageInfo().pageNumber;

      // Draw footer line
      doc.setDrawColor(200, 200, 200);
      doc.line(10, pageHeight - 18, pageWidth - 10, pageHeight - 18);

      // Footer text
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(`Total Records: ${totalRecords}  |  Generated: ${generatedDate}`, 10, pageHeight - 12);

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(50, 50, 50);
      doc.text(`${DEPARTMENT}, ${GOVERNMENT}`, pageWidth / 2, pageHeight - 7, { align: 'center' });

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(`Page ${currentPage}`, pageWidth - 10, pageHeight - 7, { align: 'right' });
    }
  });

  // AP state branding — header emblem on page 1, faint watermark on every page.
  await addApHeaderLogo(doc, { page: 1, x: LOGO_X, y: LOGO_Y, width: LOGO_SIZE, height: LOGO_SIZE });
  await addApWatermark(doc);

  // Save PDF
  doc.save(filename);
};

export const exportToDocx = async (data: any[], columns: ColumnConfig[], filename: string = 'export.docx', title?: string) => {
  // Dynamically import docx library
  const { Document, Packer, Paragraph, Table, TableRow, TableCell, TextRun, WidthType, AlignmentType, BorderStyle, VerticalAlign, HeightRule, convertInchesToTwip, Footer, PageNumber, Header } = await import('docx');

  const displayTitle = title || 'Data Export';
  const generatedDate = new Date().toLocaleString();
  const totalRecords = data.length;

  // Filter and sort columns based on export settings and order
  const exportableColumns = columns
    .filter(col => col.exportable !== false && col.columnType !== 'template' && col.columnType !== 'button')
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  // Define border style for cells
  const tableBorders = {
    top: { style: BorderStyle.SINGLE, size: 1, color: '666666' },
    bottom: { style: BorderStyle.SINGLE, size: 1, color: '666666' },
    left: { style: BorderStyle.SINGLE, size: 1, color: '666666' },
    right: { style: BorderStyle.SINGLE, size: 1, color: '666666' },
  };

  // Calculate column width (distribute evenly)
  const columnWidth = Math.floor(9000 / exportableColumns.length);

  // Create header row
  const headerRow = new TableRow({
    children: exportableColumns.map(col =>
      new TableCell({
        children: [new Paragraph({
          children: [new TextRun({
            text: col.columnName,
            bold: true,
            size: 22,
            color: 'FFFFFF',
          })],
          alignment: AlignmentType.CENTER,
          spacing: { before: 60, after: 60 },
        })],
        shading: { fill: '3B82F6' },
        borders: tableBorders,
        verticalAlign: VerticalAlign.CENTER,
        width: { size: columnWidth, type: WidthType.DXA },
      })
    ),
    tableHeader: true,
    height: { value: 400, rule: HeightRule.ATLEAST },
  });

  // Create data rows with alternating colors and sequential serial numbers
  const dataRows = data.map((row, rowIndex) =>
    new TableRow({
      children: exportableColumns.map(col => {
        const field = col.field || col.columnName;
        let value: string;

        // Generate sequential serial number for serial columns
        if (isSerialColumn(col.columnName, field)) {
          value = String(rowIndex + 1);
        } else if (col.exportValue) {
          // Use custom exportValue function if provided
          const exportedValue = col.exportValue(row);
          value = exportedValue === null || exportedValue === undefined ? '-' : String(exportedValue);
        } else {
          let rawValue = row[field];

          // Handle different data types
          if (rawValue === null || rawValue === undefined) {
            value = '-';
          } else if (typeof rawValue === 'boolean') {
            value = rawValue ? 'Yes' : 'No';
          } else if (typeof rawValue === 'object') {
            value = JSON.stringify(rawValue);
          } else {
            value = String(rawValue);
          }
        }

        return new TableCell({
          children: [new Paragraph({
            children: [new TextRun({ text: value, size: 20 })],
            spacing: { before: 40, after: 40 },
          })],
          borders: tableBorders,
          shading: { fill: rowIndex % 2 === 0 ? 'FFFFFF' : 'F3F4F6' },
          verticalAlign: VerticalAlign.CENTER,
          width: { size: columnWidth, type: WidthType.DXA },
        });
      }),
      height: { value: 340, rule: HeightRule.ATLEAST },
    })
  );

  // Create table with borders
  const table = new Table({
    rows: [headerRow, ...dataRows],
    width: { size: 100, type: WidthType.PERCENTAGE },
  });

  // Create document with footer on each page
  const doc = new Document({
    sections: [{
      properties: {
        page: {
          margin: {
            top: convertInchesToTwip(0.5),
            right: convertInchesToTwip(0.5),
            bottom: convertInchesToTwip(0.75),
            left: convertInchesToTwip(0.5),
          },
        },
      },
      footers: {
        default: new Footer({
          children: [
            new Paragraph({
              children: [
                new TextRun({ text: `Total Records: ${totalRecords}  |  Generated: ${generatedDate}`, size: 16, color: '666666' }),
              ],
              spacing: { before: 100 },
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Directorate of Town & Country Planning, Government of Andhra Pradesh', size: 18, bold: true, color: '333333' }),
                new TextRun({ text: '    |    Page ', size: 16, color: '666666' }),
                new TextRun({ children: [PageNumber.CURRENT], size: 16, color: '666666' }),
                new TextRun({ text: ' of ', size: 16, color: '666666' }),
                new TextRun({ children: [PageNumber.TOTAL_PAGES], size: 16, color: '666666' }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { before: 50 },
            }),
          ],
        }),
      },
      children: [
        new Paragraph({
          children: [new TextRun({ text: displayTitle, bold: true, size: 36 })],
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 },
        }),
        table,
      ],
    }],
  });

  // Generate and save file
  const blob = await Packer.toBlob(doc);
  saveAs(blob, filename);
};

export const calculateSummary = (data: any[], field: string, summaryType: string) => {
  const values = data.map(row => row[field]).filter(val => val !== null && val !== undefined);

  switch (summaryType) {
    case 'sum':
      return values.reduce((acc, val) => acc + (Number(val) || 0), 0);
    case 'count':
      return values.length;
    case 'avg':
      const sum = values.reduce((acc, val) => acc + (Number(val) || 0), 0);
      return values.length > 0 ? sum / values.length : 0;
    case 'max':
      return Math.max(...values.map(val => Number(val) || 0));
    case 'min':
      return Math.min(...values.map(val => Number(val) || 0));
    default:
      return 0;
  }
};
